import React from 'react'
import './Radio.css'

/**
 * Reusable Radio Group Component
 *
 * A customizable radio button group with label and error handling
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Radio group label
 * @param {string} props.name - Radio input name (required for grouping)
 * @param {Array} props.options - Array of options [{value, label}]
 * @param {string} props.value - Selected value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.layout - 'horizontal' or 'vertical' (default: 'horizontal')
 * @param {React.Ref} ref - Forwarded ref for React Hook Form compatibility
 */
const Radio = React.forwardRef(
	(
		{
			label,
			name,
			options = [],
			value,
			onChange,
			error,
			required = false,
			disabled = false,
			className = '',
			layout = 'horizontal',
			...props
		},
		ref
	) => {
		const baseClass = 'radio-group'
		const wrapperClasses = [
			baseClass,
			error && `${baseClass}--error`,
			disabled && `${baseClass}--disabled`,
			`${baseClass}--${layout}`,
			className,
		]
			.filter(Boolean)
			.join(' ')

		return (
			<div className={wrapperClasses}>
				{label && (
					<label className={`${baseClass}__label`}>
						{label}
						{required && <span className={`${baseClass}__required`}>*</span>}
					</label>
				)}

				<div className={`${baseClass}__options`}>
					{options.map((option, index) => {
						const radioId = `${name}-${option.value}`
						const isChecked = value === option.value

						return (
							<label
								key={option.value}
								htmlFor={radioId}
								className={`${baseClass}__option ${isChecked ? `${baseClass}__option--checked` : ''}`}
							>
								<input
									id={radioId}
									type="radio"
									name={name}
									value={option.value}
									checked={isChecked}
									onChange={onChange}
									disabled={disabled}
									className={`${baseClass}__input`}
									ref={ref}
									{...props}
								/>
								<span className={`${baseClass}__radio`}>
									{isChecked && <span className={`${baseClass}__radio-dot`} />}
								</span>
								<span className={`${baseClass}__option-label`}>{option.label}</span>
							</label>
						)
					})}
				</div>

				{error && <span className={`${baseClass}__error`}>{error}</span>}
			</div>
		)
	}
)

Radio.displayName = 'Radio'

export default Radio
